// ============================================================================
//  Single source of truth for the app's modules.
//  Both the Sidebar (navigation) and User Management (access rights) derive
//  from MENU below — so when you add a new form/report here, it automatically
//  appears in User Management for assigning rights. No second list to maintain.
// ============================================================================
import {
  LayoutDashboard, ShoppingCart, Package, Warehouse,
  DollarSign, BarChart3,
  FileText, RotateCcw, Truck, ClipboardList, Receipt, CreditCard,
  BookOpen, Wallet, ArrowLeftRight, Settings, Building2, Users, Factory, Landmark,
  TrendingUp, Scale,
} from 'lucide-react';

// Top-level leaves carry an explicit `group` (used to group the permission
// checkboxes). Items with `children` use their own label as the group.
export const MENU = [
  { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, group: 'General' },
  { key: 'business-profile', label: 'Business Profile', icon: Building2, group: 'General' },
  {
    key: 'masters', label: 'Masters', icon: Settings,
    children: [
      { key: 'parties', label: 'Party Master' },
      { key: 'items', label: 'Item Master' },
      { key: 'accounts', label: 'Account Master' },
      { key: 'account-group', label: 'Account Group' },
      { key: 'taxes', label: 'Tax Master' },
      { key: 'tds-sections', label: 'TDS Sections' },
      { key: 'financial-year', label: 'Financial Year' },
      { key: 'prefix-setting', label: 'Prefix Setting' },
    ],
  },
  {
    key: 'sales', label: 'Sales', icon: ShoppingCart,
    children: [
      { key: 'sales-invoice', label: 'Sales Invoice', icon: FileText },
      { key: 'sales-return', label: 'Sales Return', icon: RotateCcw },
      { key: 'delivery-challan', label: 'Delivery Challan', icon: Truck },
      { key: 'sales-order', label: 'Sales Order', icon: ClipboardList },
    ],
  },
  {
    key: 'purchase', label: 'Purchase', icon: Package,
    children: [
      { key: 'purchase-bill', label: 'Purchase Bill', icon: FileText },
      { key: 'purchase-return', label: 'Purchase Return', icon: RotateCcw },
      { key: 'purchase-order', label: 'Purchase Order', icon: ClipboardList },
    ],
  },
  {
    key: 'production', label: 'Production', icon: Factory,
    children: [
      { key: 'production-entry', label: 'Production Entry', icon: ClipboardList },
    ],
  },
  {
    key: 'inventory', label: 'Inventory', icon: Warehouse,
    children: [
      { key: 'stock-summary', label: 'Stock Summary' },
      { key: 'stock-ledger', label: 'Stock Ledger' },
      { key: 'stock-ledger-summary', label: 'Stock Ledger Summary' },
      { key: 'stock-journal', label: 'Stock Journal' },
      { key: 'stock-issue', label: 'Stock Issue to Consumption' },
      { key: 'low-stock', label: 'Low Stock Alert' },
    ],
  },
  {
    key: 'finance', label: 'Accounts', icon: DollarSign,
    children: [
      { key: 'receipt', label: 'Party Receipt Entry', icon: Receipt },
      { key: 'payment', label: 'Party Payment Entry', icon: CreditCard },
      { key: 'direct-receipt', label: 'Other Receipt Entry', icon: Receipt },
      { key: 'direct-payment', label: 'Other Payment Entry', icon: CreditCard },
      { key: 'journal', label: 'Journal Entry', icon: BookOpen },
      { key: 'expense', label: 'Expense Entry', icon: Wallet },
      { key: 'contra', label: 'Contra Entry', icon: ArrowLeftRight },
      { key: 'bank-reconcile', label: 'Bank Reconciliation', icon: Landmark },
      { key: 'profit-loss', label: 'Profit & Loss', icon: TrendingUp },
      { key: 'balance-sheet', label: 'Balance Sheet', icon: Scale },
    ],
  },
  {
    key: 'reports', label: 'Reports', icon: BarChart3,
    children: [
      { key: 'report-sales', label: 'Sales Reports' },
      { key: 'report-purchase', label: 'Purchase Reports' },
      { key: 'report-inventory', label: 'Inventory Reports' },
      { key: 'report-production', label: 'Production Reports' },
      { key: 'report-accounts', label: 'Account Reports' },
    ],
  },
  { key: 'user-management', label: 'User Management', icon: Users, group: 'Admin' },
];

// Flatten MENU into the assignable permission modules (leaf items only).
export const ALL_MODULES = MENU.flatMap(item =>
  item.children
    ? item.children.map(c => ({ key: c.key, label: c.label, group: item.label }))
    : [{ key: item.key, label: item.label, group: item.group || 'General' }]
);

export const ALL_MODULE_KEYS = ALL_MODULES.map(m => m.key);
